import React, { createContext, useContext, useState, useEffect } from "react";
import { translations } from "../translations";

const LanguageContext = createContext();

/**
 * Detect preferred language from browser (navigator.language / navigator.languages).
 * Returns 'zh' if browser is set to any Chinese locale (zh, zh-CN, zh-TW, etc.), else 'en'.
 */
const getBrowserLanguage = () => {
  if (typeof navigator === "undefined") return "en";
  const lang =
    navigator.language || (navigator.languages && navigator.languages[0]) || "";
  return lang.toLowerCase().startsWith("zh") ? "zh" : "en";
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Prefer saved user choice; otherwise use browser language (Chinese → zh, else → en)
    const saved = localStorage.getItem("language");
    if (saved === "zh" || saved === "en") return saved;
    return getBrowserLanguage();
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem("language", language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "zh" ? "en" : "zh"));
  };

  const t = (key, params = {}) => {
    const keys = key.split(".");
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    // If value is a string with placeholders like {num}, replace them
    if (typeof value === "string" && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? params[paramKey] : match;
      });
    }
    // Return the value if found, otherwise return the key as fallback
    return value !== undefined ? value : key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
